// Mobile & foldable redesign (docs/plans/mobile-foldable-redesign.md §2.1) —
// the ONE place that decides which of the three layout tiers a viewport width
// belongs to.
//
// Why a third tier exists at all: the app used to have exactly two layouts
// split at 896px (`lib/playerDock.ts`'s `MINI_BAR_MAX_WIDTH_PX`), so a Galaxy
// Z Fold's 752px inner screen got the phone layout stretched to twice a
// phone's width — and on /live the stacked composition left ~30px for the
// channel list. Meanwhile the Guide's own compact switch sat at 640 and
// `.main`'s padding switch at 768: three different notions of "mobile"
// between 640 and 896.
//
// Root CLAUDE.md rule #2 ("one canonical resolver across every layer that
// performs the same domain transformation"): every JS "am I mobile?" read
// goes through `tierForWidth` / `useLayoutTier`, and CSS mirrors it with
// exactly two shell boundaries — `(min-width: 640px)` and
// `(min-width: 1024px)`. A phone-only CSS rule is written `max-width: 639px`,
// never the inclusive `640px`, so 640 is never in both tiers at once.
// `layoutTier.browser.test.ts` pins the JS and CSS halves to each other at
// 639/640 and 1023/1024.

/** Widths at or below this are the phone tier. 640 belongs to large-mobile. */
export const PHONE_MAX_WIDTH_PX = 639;

/** Widths at or below this (and above `PHONE_MAX_WIDTH_PX`) are large-mobile. */
export const LARGE_MOBILE_MAX_WIDTH_PX = 1023;

/**
 * - `phone` — `< 640`: BottomTabBar, single column, stacked Live.
 * - `large-mobile` — `640–1023`: AppRail locked collapsed, two-pane Live,
 *   Guide timeline, 4-up Home strips. Foldables unfolded, tablets, phones
 *   in landscape.
 * - `desktop` — `>= 1024`: unchanged from before this plan.
 */
export type LayoutTier = 'phone' | 'large-mobile' | 'desktop';

/**
 * The tier a CSS-pixel viewport width belongs to.
 *
 * A non-finite width (SSR, an unset `window`) resolves to `desktop` — the same
 * default `useLayoutTier` starts from, so a value that was never measured at
 * all degrades to the layout that assumes the most room rather than to a phone
 * layout on a desktop. A width of `0` is NOT that case: `getBoundingClientRect`
 * on a detached or `display:none` box returns a finite 0, which is a real
 * measurement of a zero-width box, and the narrowest layout is the honest
 * answer for it.
 */
export function tierForWidth(widthPx: number): LayoutTier {
    if (!Number.isFinite(widthPx)) return 'desktop';
    if (widthPx <= PHONE_MAX_WIDTH_PX) return 'phone';
    if (widthPx <= LARGE_MOBILE_MAX_WIDTH_PX) return 'large-mobile';
    return 'desktop';
}

/**
 * True when the shell docks the mini player as a fixed bar (phone: above the
 * BottomTabBar; large-mobile: at the bottom of the content column, right of
 * the rail) rather than as the draggable desktop corner tile.
 *
 * This replaces `lib/playerDock.ts`'s `isMiniBarViewport` — same question,
 * now asked of the tier instead of a second width literal.
 */
export function isDockedBarTier(tier: LayoutTier): boolean {
    return tier !== 'desktop';
}
